import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Paperclip, Plus, Receipt, Trash2 } from 'lucide-react';
import { formatMoney, type Expense, type ExpenseSummary } from '@bakery/shared';

import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  cx,
  EmptyState,
  Field,
  Input,
  Modal,
  MoneyInput,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '../components/ui.js';

/**
 * What the shop spends, a month at a time.
 *
 * Rent, the bills, a boosted post. None of it was recorded anywhere, so the
 * dashboard reported revenue less the cost of the flour and called it profit —
 * a fair measure of whether a loaf is priced right, and a misleading answer to
 * "did we make money this month".
 *
 * Two of the headings are not typed in here. Wages come from payroll and
 * ingredients from the stock room, because both are already recorded there and
 * entering them twice would mean two sets of figures drifting apart. They show
 * as rows all the same, since a total that quietly left them out would answer a
 * narrower question than the one being asked.
 */
export function ExpensesScreen() {
  const [month, setMonth] = useState(() => new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 7));
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  /** A repeating bill being carried forward: its figures, but a new entry. */
  const [prefill, setPrefill] = useState<Expense | null>(null);

  const summary = useQuery({
    queryKey: ['expenses', month],
    queryFn: () => api.expenses.summary(month),
  });

  const shift = (by: number) => {
    const [year, index] = month.split('-').map(Number);
    const moved = new Date(Date.UTC(year!, index! - 1 + by, 1));
    setMonth(moved.toISOString().slice(0, 7));
  };

  const label = useMemo(
    () => new Date(`${month}-01T12:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    [month],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-lg font-semibold">Expenses</h1>
          <p className="text-xs text-[var(--color-ink-soft)]">
            {label}
            {summary.data && <> · {formatMoney(summary.data.total)} spent</>}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" aria-label="Previous month" onClick={() => shift(-1)} icon={<ChevronLeft className="size-4" />} />
          <Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} className="w-40" />
          <Button size="sm" variant="ghost" aria-label="Next month" onClick={() => shift(1)} icon={<ChevronRight className="size-4" />} />
          <Button size="sm" variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
            Add expense
          </Button>
        </div>
      </header>

      {summary.isLoading && <Skeleton className="m-4 h-64" />}
      {summary.data && (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <Totals summary={summary.data} />
          <DueAgain summary={summary.data} onUse={setPrefill} />
          <Entries summary={summary.data} month={month} onEdit={setEditing} />
        </div>
      )}

      {adding && <ExpenseDialog month={month} onDone={() => setAdding(false)} />}
      {editing && <ExpenseDialog month={month} expense={editing} onDone={() => setEditing(null)} />}
      {prefill && <ExpenseDialog month={month} carryFrom={prefill} onDone={() => setPrefill(null)} />}
    </div>
  );
}

/** Each heading's share of the month, biggest first. */
function Totals({ summary }: { summary: ExpenseSummary }) {
  if (summary.lines.length === 0) {
    return (
      <EmptyState
        icon={<Receipt className="size-10" />}
        title="Nothing recorded this month"
        description="Add the rent, a bill or an advert and it will show here — and come off the profit on the dashboard."
      />
    );
  }

  const largest = Math.max(...summary.lines.map((line) => line.total));

  return (
    <Card className="overflow-hidden">
      {summary.lines.map((line) => (
        <div key={line.key} className="flex items-center gap-3 border-b border-[var(--color-line)] px-4 py-2.5 last:border-b-0">
          <span className="w-40 shrink-0 text-sm font-medium">
            {line.name}
            {/*
              * Said on the row rather than in a footnote. Somebody looking for
              * where to change the wage bill should be told it is payroll, not
              * left wondering why this one will not open.
              */}
            {!line.editable && (
              <span className="ml-2 text-[11px] font-normal text-[var(--color-ink-faint)]">
                from {line.source === 'payroll' ? 'payroll' : 'stock room'}
              </span>
            )}
          </span>
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-sunken)]">
            <span
              className={cx('block h-full rounded-full', line.editable ? 'bg-[var(--color-crust-500)]' : 'bg-[var(--color-wheat-500)]')}
              style={{ width: `${largest > 0 ? Math.max(2, (line.total / largest) * 100) : 0}%` }}
            />
          </span>
          <span className="tabular w-28 shrink-0 text-right text-sm">{formatMoney(line.total)}</span>
        </div>
      ))}
      <div className="flex items-center gap-3 bg-[var(--color-surface-sunken)] px-4 py-3">
        <span className="w-40 shrink-0 text-sm font-semibold">Total</span>
        <span className="flex-1" />
        <span className="tabular w-28 shrink-0 text-right text-sm font-semibold">{formatMoney(summary.total)}</span>
      </div>
    </Card>
  );
}

function Entries({
  summary,
  month,
  onEdit,
}: {
  summary: ExpenseSummary;
  month: string;
  onEdit: (expense: Expense) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: (id: string) => api.expenses.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      // The dashboard's profit is built on these, so it is wrong until it asks again.
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove it', body: error.message }),
  });

  if (summary.expenses.length === 0) return null;

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">
        What was paid <span className="font-normal text-[var(--color-ink-faint)]">· {summary.expenses.length}</span>
      </h2>
      <Card className="divide-y divide-[var(--color-line)] overflow-hidden">
        {summary.expenses.map((expense) => (
          <div key={expense.id} className="flex items-start gap-3 px-4 py-2.5">
            <span className="tabular w-16 shrink-0 text-xs text-[var(--color-ink-soft)]">
              {new Date(`${expense.spentOn}T12:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
            </span>
            <span className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onEdit(expense)}
                className="block text-left text-sm font-medium underline decoration-dotted decoration-transparent underline-offset-2 hover:decoration-[var(--color-ink-soft)]"
              >
                {expense.categoryName}
              </button>
              {/* The note is the point of the row — always on, never a hover. */}
              {expense.notes && <span className="block text-xs text-[var(--color-ink-soft)]">{expense.notes}</span>}
              <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                {expense.paidBy && <Badge>{expense.paidBy}</Badge>}
                {expense.repeatsMonthly && <Badge tone="info">Every month</Badge>}
                {expense.hasReceipt && <ReceiptLink expense={expense} />}
              </span>
            </span>
            <span className="tabular shrink-0 text-sm">{formatMoney(expense.amount)}</span>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Remove the ${expense.categoryName} expense`}
              className="shrink-0 px-2 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
              onClick={() => remove.mutate(expense.id)}
              icon={<Trash2 className="size-4" />}
            />
          </div>
        ))}
      </Card>
      <p className="mt-2 text-xs text-[var(--color-ink-faint)]">
        Wages and ingredients are counted above but recorded elsewhere — change them in Team &amp; payroll and the stock
        room, so there is only ever one copy of each figure.
      </p>
      {month !== summary.month && <span className="sr-only">Showing {summary.month}</span>}
    </section>
  );
}

/**
 * Last month's repeating bills, not yet paid this one.
 *
 * Offered with the figures filled in rather than added automatically. The
 * electricity bill is never twice the same amount, and a month that quietly
 * carried last month's number would be wrong in a way nobody would notice.
 */
function DueAgain({ summary, onUse }: { summary: ExpenseSummary; onUse: (expense: Expense) => void }) {
  if (summary.dueAgain.length === 0) return null;

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">Comes round every month</h2>
      <Card className="divide-y divide-[var(--color-line)] overflow-hidden">
        {summary.dueAgain.map((expense) => (
          <div key={expense.id} className="flex items-center gap-3 px-4 py-2.5">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">{expense.categoryName}</span>
              <span className="block text-xs text-[var(--color-ink-soft)]">
                {formatMoney(expense.amount)} last month{expense.notes ? ` · ${expense.notes}` : ''}
              </span>
            </span>
            <Button size="sm" icon={<Plus className="size-4" />} onClick={() => onUse(expense)}>
              Add
            </Button>
          </div>
        ))}
      </Card>
    </section>
  );
}

/** Open the kept receipt. Fetched rather than linked, because it needs a token. */
function ReceiptLink({ expense }: { expense: Expense }) {
  const toast = useToast();
  const [opening, setOpening] = useState(false);

  const open = async () => {
    setOpening(true);
    try {
      const url = await api.expenses.receiptObjectUrl(expense.id);
      window.open(url, '_blank', 'noopener');
      // Freed once the new tab has taken a copy; holding it leaks the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.push({ tone: 'danger', title: 'Could not open the receipt', body: (error as Error).message });
    } finally {
      setOpening(false);
    }
  };

  return (
    <button type="button" onClick={open} disabled={opening} className="inline-flex items-center gap-1 text-xs text-crust-600 hover:underline disabled:opacity-60">
      <Paperclip className="size-3" /> {opening ? 'Opening…' : 'Receipt'}
    </button>
  );
}

/**
 * Add or correct one expense.
 *
 * `carryFrom` is last month's repeating bill being brought forward: its figures
 * fill the form, but it saves as a new entry on this month rather than editing
 * the old one.
 */
function ExpenseDialog({
  month,
  expense,
  carryFrom,
  onDone,
}: {
  month: string;
  expense?: Expense;
  carryFrom?: Expense;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const categories = useQuery({ queryKey: ['expense-categories'], queryFn: () => api.expenses.categories() });
  const receiptInput = useRef<HTMLInputElement>(null);

  const starting = expense ?? carryFrom;
  const [form, setForm] = useState({
    categoryId: starting?.categoryId ?? '',
    // A new expense is usually today's, unless a past month is being filled in.
    spentOn: expense?.spentOn ?? defaultDateFor(month),
    amount: starting?.amount ?? 0,
    notes: starting?.notes ?? '',
    paidBy: starting?.paidBy ?? '',
    repeatsMonthly: starting?.repeatsMonthly ?? false,
  });

  const categoryId = form.categoryId || categories.data?.[0]?.id || '';

  const save = useMutation({
    mutationFn: () => {
      const body = {
        categoryId,
        spentOn: form.spentOn,
        amount: form.amount,
        notes: form.notes || null,
        paidBy: form.paidBy || null,
        repeatsMonthly: form.repeatsMonthly,
      };
      return expense ? api.expenses.update(expense.id, body) : api.expenses.create(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onDone();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  /*
   * The file goes up as base64, the same way an inbox attachment does, so
   * nothing new has to parse multipart on the server.
   */
  const attach = useMutation({
    mutationFn: async (file: File) => {
      if (!expense) throw new Error('Save the expense first, then attach the receipt.');
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('That file could not be read.'));
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.readAsDataURL(file);
      });
      return api.expenses.attachReceipt(expense.id, file.type, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.push({ tone: 'ok', title: 'Receipt kept' });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not attach it', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onDone}
      title={expense ? 'Edit expense' : 'Add expense'}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!categoryId || form.amount <= 0}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="What for" required>
            <Select value={categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Amount" required>
            <MoneyInput value={form.amount} onChange={(amount) => setForm({ ...form, amount })} />
          </Field>
          <Field label="Date paid" required>
            <Input type="date" value={form.spentOn} onChange={(event) => setForm({ ...form, spentOn: event.target.value })} />
          </Field>
          <Field label="Paid by" hint="Cash, GCash, bank…">
            <Input value={form.paidBy} onChange={(event) => setForm({ ...form, paidBy: event.target.value })} />
          </Field>
        </div>

        <Field label="Notes" hint="What this was for. Worth writing — it is what makes the entry readable later.">
          <Textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </Field>

        {/*
          * Marked once, offered every month after.
          *
          * It does not create anything on its own — next month this bill
          * appears at the top of the screen with last month's figures and
          * waits to be confirmed, because the amount is rarely the same twice.
          */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.repeatsMonthly}
            onChange={(event) => setForm({ ...form, repeatsMonthly: event.target.checked })}
            className="mt-0.5 size-4 accent-[var(--color-crust-500)]"
          />
          <span>
            Comes round every month
            <span className="block text-xs text-[var(--color-ink-faint)]">
              Offered again next month with these figures, for you to confirm.
            </span>
          </span>
        </label>

        {/*
          * The receipt goes up only once the expense exists, because the file
          * is stored under its id. Saving first is one press, not two: the
          * button below saves and then uploads.
          */}
        <Field label="Receipt" hint="A photo or a PDF. Kept on the server, not in the message to anyone.">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={receiptInput}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) attach.mutate(file);
                event.target.value = '';
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              loading={attach.isPending}
              onClick={() => receiptInput.current?.click()}
              icon={<Paperclip className="size-4" />}
            >
              {expense?.hasReceipt ? 'Replace the receipt' : 'Attach a receipt'}
            </Button>
            {expense?.hasReceipt && <ReceiptLink expense={expense} />}
            {!expense && (
              <span className="text-xs text-[var(--color-ink-faint)]">Save first, then a receipt can be attached.</span>
            )}
          </div>
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Today when the month on screen is this one, otherwise that month's first day.
 *
 * Filling in September in October should not quietly date everything today and
 * drop it out of the month being worked on.
 */
function defaultDateFor(month: string): string {
  const today = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return today.startsWith(month) ? today : `${month}-01`;
}
